import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import QRCode from "react-qr-code";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import api from "../../services/api";
import { socketConnection } from "../../services/socket";

// Fluxo de pagamento de UMA fatura via PIX, em três estados:
//   review  → resumo da fatura e botão "Gerar PIX"
//   pending → QR Code / Copia e Cola, aguardando a confirmação do banco
//   paid    → pagamento confirmado pelo webhook
// Nada aqui diz "sucesso" antes da confirmação real do pagamento.

// Intervalo da checagem de reforço caso o evento do socket se perca.
const POLL_INTERVAL_MS = 10000;

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

// O PIX gerado fica guardado por fatura enquanto for válido: fechar e reabrir
// o modal mostra o MESMO código, em vez de criar uma nova cobrança.
const cacheKey = (invoiceId) => `pix-invoice-${invoiceId}`;

const readCachedPix = (invoiceId) => {
  try {
    const raw = localStorage.getItem(cacheKey(invoiceId));
    if (!raw) return null;
    const pix = JSON.parse(raw);
    if (!pix?.expiresAt || moment(pix.expiresAt).isBefore(moment())) {
      localStorage.removeItem(cacheKey(invoiceId));
      return null;
    }
    return pix;
  } catch (e) {
    return null;
  }
};

const writeCachedPix = (invoiceId, pix) => {
  try {
    localStorage.setItem(cacheKey(invoiceId), JSON.stringify(pix));
  } catch (e) {}
};

const clearCachedPix = (invoiceId) => {
  try {
    localStorage.removeItem(cacheKey(invoiceId));
  } catch (e) {}
};

const useStyles = makeStyles((theme) => ({
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  amount: {
    fontWeight: 700,
    margin: theme.spacing(1, 0),
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 0),
  },
  qrBox: {
    // Fundo branco fixo: leitores de QR falham com o código sobre fundo escuro.
    background: "#fff",
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
  },
  pixCode: {
    width: "100%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    background: theme.palette.action.hover,
    fontFamily: "monospace",
    fontSize: "0.75rem",
    wordBreak: "break-all",
    maxHeight: 64,
    overflow: "hidden",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  steps: {
    textAlign: "left",
    width: "100%",
    margin: theme.spacing(2, 0, 0),
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    "& li": { marginBottom: theme.spacing(0.5) },
  },
  waitingChip: {
    backgroundColor: theme.palette.type === "dark" ? "#4a3b00" : "#fff8e1",
    color: theme.palette.type === "dark" ? "#ffd54f" : "#e65100",
    fontWeight: 600,
  },
  overdueChip: {
    backgroundColor: theme.palette.type === "dark" ? "#4a1515" : "#ffebee",
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#c62828",
    fontWeight: 600,
  },
  openChip: {
    fontWeight: 600,
  },
  paidIcon: {
    fontSize: 72,
    color: theme.palette.success?.main || "#2e7d32",
  },
  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginTop: theme.spacing(2),
    background: theme.palette.type === "dark" ? "#4a1515" : "#ffebee",
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#c62828",
    textAlign: "left",
  },
}));

const dueInfo = (dueDate) => {
  if (!dueDate) return null;
  const days = moment(dueDate).startOf("day").diff(moment().startOf("day"), "days");
  if (days < 0) return { overdue: true, label: `Vencida há ${Math.abs(days)} dia${days === -1 ? "" : "s"}` };
  if (days === 0) return { overdue: false, label: "Vence hoje" };
  return { overdue: false, label: `Vence em ${days} dia${days === 1 ? "" : "s"}` };
};

const Countdown = ({ expiresAt, onExpire }) => {
  const [now, setNow] = useState(moment());

  useEffect(() => {
    const timer = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(timer);
  }, []);

  const remaining = Math.max(0, moment(expiresAt).diff(now, "seconds"));

  useEffect(() => {
    if (remaining === 0) onExpire();
  }, [remaining, onExpire]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <Typography variant="body2" color="textSecondary">
      Código válido por <strong>{mm}:{ss}</strong>
    </Typography>
  );
};

export default function CheckoutPage({ Invoice, onPaid }) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const invoiceId = Invoice?.id;
  const [pix, setPix] = useState(() => (invoiceId ? readCachedPix(invoiceId) : null));
  const [step, setStep] = useState(pix ? "pending" : "review");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newDueDate, setNewDueDate] = useState(null);

  const markPaid = useCallback(
    (dueDate) => {
      clearCachedPix(invoiceId);
      setNewDueDate(dueDate || null);
      setStep("paid");
      if (onPaid) onPaid();
    },
    [invoiceId, onPaid]
  );

  // Aguarda a confirmação: socket (imediato) + checagem periódica da fatura
  // como reforço, caso o evento se perca numa reconexão.
  useEffect(() => {
    if (step !== "pending" || !invoiceId) return;

    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-payment`, (data) => {
      if (data?.action !== "CONCLUIDA") return;
      if (data.invoiceId && String(data.invoiceId) !== String(invoiceId)) return;
      markPaid(data.company?.dueDate);
    });

    const poll = setInterval(async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}`);
        if (data?.status === "paid") markPaid(null);
      } catch (e) {}
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(poll);
      socket.disconnect();
    };
  }, [step, invoiceId, markPaid]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await api.post("/subscription", { invoiceId });
      const generated = {
        code: data.qrcode?.qrcode,
        value: data.invoice?.value ?? Invoice.value,
        expiresAt: data.expiresAt,
      };
      writeCachedPix(invoiceId, generated);
      setPix(generated);
      setExpired(false);
      setStep("pending");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "Não foi possível gerar o PIX agora. Tente novamente em instantes."
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleExpire = useCallback(() => {
    clearCachedPix(invoiceId);
    setExpired(true);
  }, [invoiceId]);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (!Invoice) {
    return (
      <Typography align="center" color="textSecondary" style={{ padding: 24 }}>
        Nenhuma fatura selecionada.
      </Typography>
    );
  }

  const due = dueInfo(Invoice.dueDate);

  if (step === "paid") {
    return (
      <Box className={classes.center} py={2}>
        <CheckCircleIcon className={classes.paidIcon} />
        <Typography variant="h5" style={{ fontWeight: 700, marginTop: 8 }}>
          Pagamento confirmado!
        </Typography>
        <Typography color="textSecondary" style={{ marginTop: 8 }}>
          {newDueDate
            ? <>Sua licença está válida até <strong>{moment(newDueDate).format("DD/MM/YYYY")}</strong>.</>
            : "Sua licença foi renovada."}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          style={{ marginTop: 24 }}
          // Recarrega o app para que licença e bloqueio reflitam o pagamento.
          onClick={() => window.location.assign("/")}
        >
          Voltar ao sistema
        </Button>
      </Box>
    );
  }

  if (step === "pending" && pix) {
    const copyButton = (
      <CopyToClipboard text={pix.code || ""} onCopy={handleCopy}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={expired}
          startIcon={copied ? <CheckIcon /> : <FileCopyOutlinedIcon />}
        >
          {copied ? "Código copiado!" : "Copiar código PIX"}
        </Button>
      </CopyToClipboard>
    );

    return (
      <Box className={classes.center}>
        <Chip
          icon={<HourglassEmptyIcon style={{ color: "inherit" }} />}
          label="Aguardando pagamento"
          className={classes.waitingChip}
        />
        <Typography variant="h4" className={classes.amount}>
          {fmtBRL(pix.value)}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {Invoice.detail || "Fatura"} · #{Invoice.id}
        </Typography>

        {expired ? (
          <>
            <div className={classes.errorBox} style={{ width: "100%" }}>
              <ErrorOutlineIcon fontSize="small" />
              <Typography variant="body2">
                Este código PIX expirou. Gere um novo para pagar.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              style={{ marginTop: 16 }}
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? <CircularProgress size={24} color="inherit" /> : "Gerar novo código"}
            </Button>
          </>
        ) : (
          <>
            {/* No celular, o Copia e Cola é o caminho principal: vem primeiro. */}
            {isMobile && <Box width="100%" mt={2}>{copyButton}</Box>}
            <div className={classes.qrBox}>
              <QRCode value={pix.code || ""} size={isMobile ? 180 : 220} />
            </div>
            <div className={classes.pixCode}>{pix.code}</div>
            {!isMobile && copyButton}
            <Box mt={1.5}>
              <Countdown expiresAt={pix.expiresAt} onExpire={handleExpire} />
            </Box>
            <ol className={classes.steps}>
              <li>Abra o app do seu banco e escolha <strong>Pix</strong>.</li>
              <li>Escaneie o QR Code ou use <strong>Pix Copia e Cola</strong>.</li>
              <li>Confirme o pagamento. A liberação é automática em poucos segundos.</li>
            </ol>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
              Você pode fechar esta janela: ao reabrir a fatura, o mesmo código continua disponível.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.center} mb={2}>
        <Typography variant="body2" color="textSecondary">
          Valor a pagar
        </Typography>
        <Typography variant="h4" className={classes.amount}>
          {fmtBRL(Invoice.value)}
        </Typography>
        {due && (
          <Chip
            size="small"
            label={due.label}
            className={due.overdue ? classes.overdueChip : classes.openChip}
          />
        )}
      </Box>

      <Divider />
      <div className={classes.summaryRow}>
        <Typography color="textSecondary">Descrição</Typography>
        <Typography>{Invoice.detail || "Fatura"}</Typography>
      </div>
      <div className={classes.summaryRow}>
        <Typography color="textSecondary">Fatura</Typography>
        <Typography>#{Invoice.id}</Typography>
      </div>
      <div className={classes.summaryRow}>
        <Typography color="textSecondary">Vencimento</Typography>
        <Typography>
          {Invoice.dueDate ? moment(Invoice.dueDate).format("DD/MM/YYYY") : "—"}
        </Typography>
      </div>
      <div className={classes.summaryRow}>
        <Typography color="textSecondary">Forma de pagamento</Typography>
        <Typography>PIX</Typography>
      </div>
      <Divider />

      {error && (
        <div className={classes.errorBox}>
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">{error}</Typography>
        </div>
      )}

      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        style={{ marginTop: 24 }}
        onClick={handleGenerate}
        disabled={generating}
      >
        {generating ? <CircularProgress size={24} color="inherit" /> : "Gerar PIX"}
      </Button>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        align="center"
        style={{ marginTop: 8 }}
      >
        O código PIX vale por 1 hora. A licença é liberada assim que o banco confirmar o pagamento.
      </Typography>
    </Box>
  );
}
